import { FC, memo } from 'react';
import { useForm } from 'react-hook-form';
import { Project, ProjectCreateInput, ProjectStatus } from '../../types/project';
import styles from './ProjectModal.module.css';

interface ProjectModalProps {
  project?: Project;
  onSubmit: (data: ProjectCreateInput) => Promise<void>;
  onClose: () => void;
}

interface ProjectFormData {
  Name: string;
  deadline: string;
  progress_rate: number;
  delay_days: number;
  status: ProjectStatus;
  memo: string;
}

const statusOptions: { value: ProjectStatus; label: string }[] = [
  { value: 'good', label: '良好' },
  { value: 'normal', label: '普通' },
  { value: 'warning', label: '炎上の可能性あり' },
  { value: 'danger', label: '炎上中' },
];

export const ProjectModal: FC<ProjectModalProps> = memo(({ project, onSubmit, onClose }) => {
  const isEdit = !!project;
  const defaultValues: ProjectFormData = {
    Name: project?.Name || '',
    deadline: project?.deadline || '',
    progress_rate: project?.progress_rate || 0,
    delay_days: project?.delay_days || 0,
    status: project?.status || 'normal',
    memo: project?.memo || '',
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProjectFormData>({
    defaultValues,
  });

  const onFormSubmit = async (data: ProjectFormData) => {
    try {
      await onSubmit({
        ...data,
        deadline: data.deadline || null,
        memo: data.memo || null,
      });
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <h2 className={styles.title}>{isEdit ? '案件編集' : '新規案件作成'}</h2>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="project-name">
              案件名 *
            </label>
            <input
              id="project-name"
              className={styles.input}
              {...register('Name', { required: '案件名は必須です' })}
            />
            {errors.Name && (
              <span className={styles.error}>{errors.Name.message}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="project-deadline">
              期限
            </label>
            <input
              id="project-deadline"
              type="date"
              className={styles.input}
              {...register('deadline')}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="project-progress">
              進捗率
            </label>
            <input
              id="project-progress"
              type="number"
              min="0"
              max="100"
              className={styles.input}
              {...register('progress_rate', {
                min: { value: 0, message: '進捗率は0以上である必要があります' },
                max: { value: 100, message: '進捗率は100以下である必要があります' },
              })}
            />
            {errors.progress_rate && (
              <span className={styles.error}>{errors.progress_rate.message}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="project-delay">
              遅延日数
            </label>
            <input
              id="project-delay"
              type="number"
              className={styles.input}
              {...register('delay_days')}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="project-status">
              ステータス
            </label>
            <select
              id="project-status"
              className={styles.select}
              {...register('status')}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="project-memo">
              メモ
            </label>
            <textarea
              id="project-memo"
              className={styles.textarea}
              rows={3}
              {...register('memo')}
            />
          </div>

          <div className={styles.buttons}>
            <button type="submit" className={styles.submitButton}>
              {isEdit ? '更新' : '作成'}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

ProjectModal.displayName = 'ProjectModal';